// Agrupamentos de Faturamento (#5781, projeto 9) — onde o grupo de UCs que fecha e cobra junto
// é criado e mantido. Primeiro caso: as Secretarias da Prefeitura de Morrinhos.
//
// A gravação do vínculo NUNCA sai daqui direto na tabela: vai pelas RPCs vincular_ucs_agrupamento
// e desvincular_ucs_agrupamento, que gravam o histórico na mesma transação. A regra (pendência,
// plano de importação, leitura da resposta) está em src/lib/agrupamentoFaturamento.ts, com testes.
// Acesso: adm, gerente e analista (rota e menu).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, Building2, CalendarDays, CheckCircle2, ChevronRight, ClipboardCheck, FileArchive, History, Layers, Pencil,
  Plus, RefreshCw, Search, Upload, UserMinus, X,
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import InfoTip from '../../src/components/InfoTip';
import ConfirmModal from '../../src/components/ConfirmModal';
import TablePagination from '../../src/components/TablePagination';
import AgrupamentoFaturamentoModal from './AgrupamentoFaturamentoModal';
import ImportarAgrupamentoModal from './ImportarAgrupamentoModal';
import ImportarBorderoModal from './ImportarBorderoModal';
import AcompanhamentoMensalAgrupamento from './AcompanhamentoMensalAgrupamento';
import FeriadosAgrupamentoPanel from './FeriadosAgrupamentoPanel';
import AuditoriaCicloAgrupamentos from './AuditoriaCicloAgrupamentos';
import { composeEnderecoUC } from './UCSearchPicker';
import {
  cnpjsDeAgrupamento,
  interpretarResultadoVinculo,
  normalizarNumeroUc,
  resumoVinculo,
  ucsPendentesDeAgrupamento,
  type UcParaPendencia,
} from '../../src/lib/agrupamentoFaturamento';
import { mesDeOrdem, ordemMes } from '../../src/lib/conferenciaFaturamento';
import type { AgrupamentoFaturamento, AgrupamentoFaturamentoUcLog } from '../../types';

const PAGINA_BANCO = 1000;

const ESTILO_CAMPO =
  'rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-primary dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200';

interface UcLista extends UcParaPendencia {
  id: number;
  numero_uc: string;
  /** Número antigo: a fatura do mês pode ter vindo por ele (#5783). */
  outro_numero_uc?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  endereco?: string | null;
  situacao?: string | null;
}

const CAMPOS_UC =
  'id, numero_uc, outro_numero_uc, nome_cliente, cpf_cnpj_cliente, cpf_cnpj_titular, empresa_parceira_id, logradouro, numero, bairro, cidade, estado, endereco, situacao, rateio_unidade_geradora_id';

/** O PostgREST corta em 1.000 linhas: sem paginar, o cadastro (1.816 UCs) vem incompleto sem avisar. */
async function buscarTudo<T>(montar: (de: number, ate: number) => PromiseLike<{ data: T[] | null; error: any }>): Promise<T[]> {
  const todas: T[] = [];
  for (let de = 0; ; de += PAGINA_BANCO) {
    const { data, error } = await montar(de, de + PAGINA_BANCO - 1);
    if (error) throw error;
    todas.push(...(data ?? []));
    if (!data || data.length < PAGINA_BANCO) return todas;
  }
}

const cnpjBr = (v: string | null | undefined) => {
  const d = String(v ?? '').replace(/\D/g, '');
  return d.length === 14 ? d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : (v || '—');
};

const dataHoraBr = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

type Aba = 'grupo' | 'pendentes' | 'historico' | 'feriados' | 'auditoria';
/** Dentro do agrupamento: cadastro das UCs ou o fechamento de um mês (#5783). */
type AbaGrupo = 'cadastro' | 'mensal';

const AgrupamentosFaturamentoView: React.FC = () => {
  const [agrupamentos, setAgrupamentos] = useState<AgrupamentoFaturamento[]>([]);
  const [vinculos, setVinculos] = useState<{ unidade_consumidora_id: number; agrupamento_id: number }[]>([]);
  const [ucs, setUcs] = useState<UcLista[]>([]);
  const [historico, setHistorico] = useState<AgrupamentoFaturamentoUcLog[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // `?agrupamento=52&mes=SET/2026` (card da Secretaria em Faturas e Cobranças): abre direto o
  // acompanhamento mensal daquele mês. Só o valor inicial; depois quem manda é a tela.
  const [searchParams] = useSearchParams();
  const [link] = useState(() => {
    const id = Number(searchParams.get('agrupamento'));
    const ordem = ordemMes(searchParams.get('mes'));
    return {
      agrupamentoId: Number.isInteger(id) && id > 0 ? id : null,
      mes: ordem == null ? null : mesDeOrdem(ordem),
    };
  });
  const [selecionadoId, setSelecionadoId] = useState<number | null>(link.agrupamentoId);
  const [aba, setAba] = useState<Aba>('grupo');
  const [abaGrupo, setAbaGrupo] = useState<AbaGrupo>(link.agrupamentoId ? 'mensal' : 'cadastro');
  const [busca, setBusca] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(true);
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [pagina, setPagina] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [destino, setDestino] = useState('');
  const [agindo, setAgindo] = useState(false);

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<AgrupamentoFaturamento | null>(null);
  const [importarAberto, setImportarAberto] = useState(false);
  // Borderô de agrupamento (#6208): o .7z da Equatorial com as faturas do mês. Ficava em Faturas
  // e Cobranças; veio para cá porque é aqui que o resultado dele é acompanhado e fechado.
  const [borderoAberto, setBorderoAberto] = useState(false);
  /** Muda a cada importação de borderô: o acompanhamento mensal relê as faturas do mês. */
  const [sinalFaturas, setSinalFaturas] = useState(0);
  const [confirmacao, setConfirmacao] = useState<{ titulo: string; mensagem: string; acao: () => Promise<void> } | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [gs, vs, us, hs, cs] = await Promise.all([
        supabase.from('agrupamentos_faturamento').select('*').order('nome'),
        buscarTudo<{ unidade_consumidora_id: number; agrupamento_id: number }>((de, ate) =>
          supabase.from('agrupamento_faturamento_ucs').select('unidade_consumidora_id, agrupamento_id')
            .order('unidade_consumidora_id').range(de, ate)),
        buscarTudo<UcLista>((de, ate) =>
          supabase.from('unidades_consumidoras').select(CAMPOS_UC).order('id').range(de, ate) as any),
        supabase.from('agrupamento_faturamento_ucs_log').select('*').order('id', { ascending: false }).limit(200),
        // Um agrupamento pode ter vários códigos na concessionária (a SAP da Prefeitura tem dois).
        supabase.from('agrupamento_codigos_concessionaria').select('agrupamento_id, codigo').order('codigo'),
      ]);
      if (gs.error) throw gs.error;
      if (hs.error) throw hs.error;
      if (cs.error) throw cs.error;
      const codigosPorGrupo = new Map<number, string[]>();
      for (const c of (cs.data ?? []) as { agrupamento_id: number; codigo: string }[]) {
        codigosPorGrupo.set(c.agrupamento_id, [...(codigosPorGrupo.get(c.agrupamento_id) ?? []), c.codigo]);
      }
      setAgrupamentos(((gs.data ?? []) as AgrupamentoFaturamento[])
        .map(a => ({ ...a, codigos: codigosPorGrupo.get(a.id) ?? [] })));
      setVinculos(vs);
      setUcs(us);
      setHistorico((hs.data ?? []) as AgrupamentoFaturamentoUcLog[]);
    } catch (e: any) {
      console.error('Agrupamentos de faturamento:', e);
      setErro(e?.message || 'Não foi possível carregar os agrupamentos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { setMarcadas(new Set()); setPagina(0); setDestino(''); }, [selecionadoId, aba, busca]);

  const vinculoPorUc = useMemo(
    () => new Map(vinculos.map(v => [v.unidade_consumidora_id, v.agrupamento_id])),
    [vinculos],
  );
  const ucPorId = useMemo(() => new Map(ucs.map(u => [u.id, u])), [ucs]);
  const nomeGrupo = useCallback(
    (id: number | null | undefined) => (id == null ? '—' : agrupamentos.find(a => a.id === id)?.nome ?? `#${id}`),
    [agrupamentos],
  );

  const contagemPorGrupo = useMemo(() => {
    const m = new Map<number, number>();
    for (const v of vinculos) m.set(v.agrupamento_id, (m.get(v.agrupamento_id) ?? 0) + 1);
    return m;
  }, [vinculos]);

  const pendentes = useMemo(() => {
    const ativos = agrupamentos.filter(a => a.ativo);
    const vinculadas = vinculos.map(v => ucPorId.get(v.unidade_consumidora_id)).filter(Boolean) as UcLista[];
    const ativasIds = new Set(vinculos.filter(v => ativos.some(a => a.id === v.agrupamento_id)).map(v => v.unidade_consumidora_id));
    const cnpjs = cnpjsDeAgrupamento(ativos, vinculadas.filter(u => ativasIds.has(u.id)));
    return ucsPendentesDeAgrupamento(ucs, vinculoPorUc.keys(), cnpjs) as UcLista[];
  }, [agrupamentos, vinculos, ucs, ucPorId, vinculoPorUc]);

  /** CNPJs que existem no cadastro de UCs. Serve para avisar quando o CNPJ do pagador foi digitado
   *  errado: sem isso, a lista de pendências fica vazia e ninguém descobre o porquê. */
  const cnpjsDoCadastro = useMemo(() => {
    const s = new Set<string>();
    for (const u of ucs) {
      for (const v of [u.cpf_cnpj_cliente, u.cpf_cnpj_titular]) {
        const d = String(v ?? '').replace(/\D/g, '');
        if (d) s.add(d);
      }
    }
    return s;
  }, [ucs]);

  const selecionado = agrupamentos.find(a => a.id === selecionadoId) ?? null;
  const cnpjSemUcNoCadastro = !!selecionado && !cnpjsDoCadastro.has(selecionado.cnpj_pagador);

  /** UCs do agrupamento selecionado, na forma que o acompanhamento mensal (#5783) consome. */
  const ucsDoSelecionado = useMemo(
    () => (selecionadoId == null ? [] : vinculos
      .filter(v => v.agrupamento_id === selecionadoId)
      .map(v => ucPorId.get(v.unidade_consumidora_id))
      .filter(Boolean) as UcLista[]),
    [selecionadoId, vinculos, ucPorId],
  );
  /** UCs de um agrupamento qualquer: o borderô usa para apontar as que não vieram no arquivo. */
  const ucsDoAgrupamento = useCallback(
    (agrupamentoId: number) => vinculos
      .filter(v => v.agrupamento_id === agrupamentoId)
      .map(v => ucPorId.get(v.unidade_consumidora_id))
      .filter(Boolean) as UcLista[],
    [vinculos, ucPorId],
  );
  const visiveisNaLista = mostrarInativos ? agrupamentos : agrupamentos.filter(a => a.ativo);

  const linhas = useMemo(() => {
    const base = aba === 'pendentes'
      ? pendentes
      : selecionadoId == null ? []
      : vinculos.filter(v => v.agrupamento_id === selecionadoId)
          .map(v => ucPorId.get(v.unidade_consumidora_id))
          .filter(Boolean) as UcLista[];
    const termo = busca.trim().toLowerCase();
    if (!termo) return base;
    const termoNum = normalizarNumeroUc(termo);
    return base.filter(u =>
      (u.nome_cliente ?? '').toLowerCase().includes(termo) ||
      (!!termoNum && normalizarNumeroUc(u.numero_uc).includes(termoNum)),
    );
  }, [aba, pendentes, selecionadoId, vinculos, ucPorId, busca]);

  const visiveis = linhas.slice(pagina * pageSize, pagina * pageSize + pageSize);
  const todasVisiveisMarcadas = visiveis.length > 0 && visiveis.every(u => marcadas.has(u.id));

  const alternarTodas = () => {
    setMarcadas(atual => {
      const nova = new Set(atual);
      if (todasVisiveisMarcadas) visiveis.forEach(u => nova.delete(u.id));
      else visiveis.forEach(u => nova.add(u.id));
      return nova;
    });
  };

  /** Toda gravação passa por aqui: mostra o retorno real do banco e recarrega. */
  const executar = useCallback(async (fn: () => Promise<string>) => {
    setAgindo(true);
    setErro(null);
    setAviso(null);
    try {
      const msg = await fn();
      setAviso(msg);
      setMarcadas(new Set());
      await carregar();
    } catch (e: any) {
      setErro(e?.code === '42501' ? 'Seu usuário não tem permissão para alterar agrupamentos.' : (e?.message || 'A ação falhou.'));
    } finally {
      setAgindo(false);
    }
  }, [carregar]);

  const vincularMarcadas = (agrupamentoId: number) => executar(async () => {
    const { data, error } = await supabase.rpc('vincular_ucs_agrupamento', {
      p_agrupamento_id: agrupamentoId,
      p_uc_ids: [...marcadas],
      p_motivo: null,
    });
    if (error) throw error;
    return resumoVinculo(interpretarResultadoVinculo(data));
  });

  const desvincularMarcadas = () => executar(async () => {
    const { data, error } = await supabase.rpc('desvincular_ucs_agrupamento', { p_uc_ids: [...marcadas], p_motivo: null });
    if (error) throw error;
    return resumoVinculo(interpretarResultadoVinculo(data));
  });

  const alternarAtivo = (a: AgrupamentoFaturamento) => {
    const n = contagemPorGrupo.get(a.id) ?? 0;
    setConfirmacao({
      titulo: a.ativo ? 'Desativar agrupamento' : 'Reativar agrupamento',
      mensagem: a.ativo
        ? `"${a.nome}" deixa de receber UCs e as ${n} UC(s) já vinculadas ficam paradas: não voltam ao faturamento individual e não entram em fechamento. Continuar?`
        : `"${a.nome}" volta a receber UCs e as ${n} UC(s) vinculadas voltam a contar no fechamento. Continuar?`,
      acao: () => executar(async () => {
        const { data, error } = await supabase.from('agrupamentos_faturamento')
          .update({ ativo: !a.ativo }).eq('id', a.id).eq('atualizado_em', a.atualizado_em).select('id');
        if (error) throw error;
        if (!data || data.length === 0) throw new Error('Outra pessoa alterou este agrupamento. Atualize a tela e tente de novo.');
        return a.ativo ? 'Agrupamento desativado.' : 'Agrupamento reativado.';
      }),
    });
  };

  if (carregando && agrupamentos.length === 0 && !erro) {
    return <LoadingSpinner label="Carregando agrupamentos de faturamento..." />;
  }

  return (
    <div className="space-y-5 p-4 pb-24 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
            <Layers size={22} className="text-primary" />
            Agrupamentos de Faturamento
            <InfoTip label="O que é um agrupamento">
              <p className="mb-2">
                Um grupo de UCs do mesmo cliente que <strong>fecha e cobra junto</strong>: um boleto para várias
                faturas. O primeiro caso são as Secretarias da Prefeitura de Morrinhos.
              </p>
              <p className="mb-2">Cada UC fica em no máximo um agrupamento. UC sem agrupamento continua no fluxo individual, como sempre.</p>
              <p>Agrupamento não se apaga: desativa. UC de empresa parceira não entra.</p>
            </InfoTip>
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {agrupamentos.length} agrupamento(s) · {vinculos.length} UC(s) vinculada(s)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={carregar} disabled={carregando || agindo} aria-busy={carregando}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
            <RefreshCw size={15} className={carregando ? 'animate-spin' : ''} /> Atualizar
          </button>
          <button onClick={() => setImportarAberto(true)} disabled={agrupamentos.length === 0}
            title={agrupamentos.length === 0 ? 'Crie um agrupamento antes de importar' : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
            <Upload size={15} /> Importar planilha
          </button>
          <button onClick={() => setBorderoAberto(true)} disabled={agrupamentos.length === 0}
            title={agrupamentos.length === 0 ? 'Crie um agrupamento antes de importar' : 'O arquivo .7z que a Equatorial manda por e-mail com as faturas do agrupamento'}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
            <FileArchive size={15} /> Importar borderô de agrupamento
          </button>
          <button onClick={() => { setEditando(null); setModalAberto(true); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90">
            <Plus size={15} /> Novo agrupamento
          </button>
        </div>
      </header>

      {erro && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
          {erro}{' '}
          <button onClick={carregar} className="font-semibold underline">Tentar de novo</button>
        </div>
      )}
      {aviso && (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-300" role="status">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <p className="flex-1">{aviso}</p>
          <button onClick={() => setAviso(null)} aria-label="Fechar aviso" className="text-emerald-700 dark:text-emerald-400"><X size={15} /></button>
        </div>
      )}

      {pendentes.length > 0 && (
        <button type="button" onClick={() => setAba('pendentes')}
          className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left ${aba === 'pendentes' ? 'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20' : 'border-amber-200 bg-amber-50/60 hover:border-amber-400 dark:border-amber-900 dark:bg-amber-900/10'}`}
          aria-pressed={aba === 'pendentes'}>
          <AlertTriangle size={20} className="shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
              {pendentes.length} UC(s) do mesmo pagador sem agrupamento
            </p>
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Enquanto ficarem fora, não entram no fechamento e, portanto, não são cobradas. Clique para ver e atribuir.
            </p>
          </div>
          <ChevronRight size={18} className="text-amber-600" />
        </button>
      )}

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <aside className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Agrupamentos</h2>
            {agrupamentos.some(a => !a.ativo) && (
              <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <input type="checkbox" checked={mostrarInativos} onChange={e => setMostrarInativos(e.target.checked)} className="rounded" />
                Mostrar inativos
              </label>
            )}
          </div>
          {visiveisNaLista.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 p-6 text-center dark:border-gray-700">
              <Building2 size={22} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Nenhum agrupamento ainda</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Crie o primeiro (ex.: "Secretaria de Saúde") para depois atribuir as UCs. Até existir um agrupamento
                ativo, a tela não tem como acusar UC sem grupo.
              </p>
            </div>
          ) : visiveisNaLista.map(a => (
            <button key={a.id} type="button" onClick={() => { setSelecionadoId(a.id); setAba('grupo'); }}
              aria-current={selecionadoId === a.id && aba === 'grupo'}
              className={`w-full rounded-2xl border p-3 text-left transition-colors ${selecionadoId === a.id && aba === 'grupo' ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 hover:border-primary dark:border-gray-700'} bg-white dark:bg-gray-800/60`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">{a.nome}</span>
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold tabular-nums text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                  {contagemPorGrupo.get(a.id) ?? 0}
                </span>
              </div>
              <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                {a.codigos?.length ? `Cód. ${a.codigos.join(', ')} · ` : ''}vence dia {a.dia_vencimento}
              </p>
              {!a.ativo && <span className="mt-1 inline-block rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">Inativo</span>}
            </button>
          ))}
          <button type="button" onClick={() => setAba('historico')} aria-pressed={aba === 'historico'}
            className={`flex w-full items-center gap-2 rounded-2xl border p-3 text-left text-sm font-medium ${aba === 'historico' ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 hover:border-primary dark:border-gray-700'} bg-white text-gray-700 dark:bg-gray-800/60 dark:text-gray-200`}>
            <History size={16} /> Histórico de movimentações
          </button>
          <button type="button" onClick={() => setAba('auditoria')} aria-pressed={aba === 'auditoria'}
            className={`flex w-full items-center gap-2 rounded-2xl border p-3 text-left text-sm font-medium ${aba === 'auditoria' ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 hover:border-primary dark:border-gray-700'} bg-white text-gray-700 dark:bg-gray-800/60 dark:text-gray-200`}>
            <ClipboardCheck size={16} /> Auditoria do ciclo
          </button>
          <button type="button" onClick={() => setAba('feriados')} aria-pressed={aba === 'feriados'}
            className={`flex w-full items-center gap-2 rounded-2xl border p-3 text-left text-sm font-medium ${aba === 'feriados' ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 hover:border-primary dark:border-gray-700'} bg-white text-gray-700 dark:bg-gray-800/60 dark:text-gray-200`}>
            <CalendarDays size={16} /> Feriados e prazos
          </button>
        </aside>

        <section className="min-w-0 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/60">
          {aba === 'auditoria' ? (
            <AuditoriaCicloAgrupamentos onMudou={carregar} />
          ) : aba === 'feriados' ? (
            <FeriadosAgrupamentoPanel onMudou={carregar} />
          ) : aba === 'historico' ? (
            <>
              <h2 className="mb-1 text-sm font-bold text-gray-900 dark:text-white">Histórico de movimentações</h2>
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                Últimas 200 movimentações de todos os agrupamentos, da mais recente para a mais antiga.
              </p>
              {historico.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Nenhuma movimentação registrada ainda.</p>
              ) : (
                <ul className="divide-y divide-gray-100 text-sm dark:divide-gray-700">
                  {historico.map(h => (
                    <li key={h.id} className="flex flex-wrap items-baseline gap-x-2 py-2">
                      <span className="font-mono text-xs text-gray-900 dark:text-white">UC {h.numero_uc ?? h.unidade_consumidora_id}</span>
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        {h.acao === 'vinculada' && <>vinculada a <strong>{nomeGrupo(h.agrupamento_para_id)}</strong></>}
                        {h.acao === 'movida' && <>movida de <strong>{nomeGrupo(h.agrupamento_de_id)}</strong> para <strong>{nomeGrupo(h.agrupamento_para_id)}</strong></>}
                        {h.acao === 'desvinculada' && <>retirada de <strong>{nomeGrupo(h.agrupamento_de_id)}</strong></>}
                      </span>
                      <span className="ml-auto text-xs text-gray-400">{h.feito_por_nome ?? '—'} · {dataHoraBr(h.feito_em)}</span>
                      {h.motivo && <span className="w-full text-xs text-gray-500 dark:text-gray-400">{h.motivo}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                {aba === 'pendentes' ? (
                  <div>
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">UCs sem agrupamento</h2>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      UCs cujo CNPJ (do cliente ou do titular) é o de um agrupamento ativo, ou de alguma UC já vinculada.
                    </p>
                  </div>
                ) : selecionado ? (
                  <div>
                    <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white">
                      {selecionado.nome}
                      {!selecionado.ativo && <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">Inativo</span>}
                    </h2>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {cnpjBr(selecionado.cnpj_pagador)}
                      {selecionado.codigos?.length ? ` · cód. ${selecionado.codigos.join(', ')}` : ''}
                      {` · ${selecionado.concessionaria} · vence dia ${selecionado.dia_vencimento}`}
                    </p>
                  </div>
                ) : (
                  <h2 className="text-sm font-bold text-gray-900 dark:text-white">Selecione um agrupamento</h2>
                )}
                {selecionado && aba === 'grupo' && (
                  <div className="flex gap-2">
                    <button onClick={() => { setEditando(selecionado); setModalAberto(true); }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                      <Pencil size={14} /> Editar
                    </button>
                    <button onClick={() => alternarAtivo(selecionado)} disabled={agindo}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                      {selecionado.ativo ? 'Desativar' : 'Reativar'}
                    </button>
                  </div>
                )}
              </div>

              {aba === 'grupo' && selecionado && (
                <div className="mb-3 flex gap-1 border-b border-gray-200 dark:border-gray-700" role="tablist" aria-label="Seções do agrupamento"
                  onKeyDown={e => {
                    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                    e.preventDefault();
                    const abas: AbaGrupo[] = ['cadastro', 'mensal'];
                    const atual = abas.indexOf(abaGrupo);
                    const proximo = e.key === 'ArrowRight' ? (atual + 1) % abas.length : (atual - 1 + abas.length) % abas.length;
                    setAbaGrupo(abas[proximo]);
                    (document.getElementById(`aba-grupo-${abas[proximo]}`) as HTMLButtonElement | null)?.focus();
                  }}>
                  {([['cadastro', 'Cadastro e UCs'], ['mensal', 'Acompanhamento mensal']] as [AbaGrupo, string][]).map(([chave, rotulo]) => (
                    <button key={chave} id={`aba-grupo-${chave}`} type="button" role="tab" aria-selected={abaGrupo === chave}
                      aria-controls={`painel-grupo-${chave}`} tabIndex={abaGrupo === chave ? 0 : -1}
                      onClick={() => setAbaGrupo(chave)}
                      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${abaGrupo === chave ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                      {rotulo}
                    </button>
                  ))}
                </div>
              )}

              {aba === 'grupo' && selecionado && abaGrupo === 'mensal' && (
                <div id="painel-grupo-mensal" role="tabpanel" aria-labelledby="aba-grupo-mensal">
                  <AcompanhamentoMensalAgrupamento agrupamento={selecionado} ucs={ucsDoSelecionado} recarregarSinal={sinalFaturas}
                    mesInicial={selecionado.id === link.agrupamentoId ? link.mes : null} />
                </div>
              )}

              {aba === 'grupo' && abaGrupo === 'cadastro' && cnpjSemUcNoCadastro && (
                <div className="mb-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  <p>
                    Nenhuma UC do cadastro tem o CNPJ <strong>{cnpjBr(selecionado!.cnpj_pagador)}</strong>. Confira se
                    ele está certo: enquanto não bater, nenhuma UC aparece como pendente deste agrupamento.
                  </p>
                </div>
              )}

              {(aba === 'pendentes' || (selecionado && abaGrupo === 'cadastro')) && (
                <div id={aba === 'pendentes' ? undefined : 'painel-grupo-cadastro'} role={aba === 'pendentes' ? undefined : 'tabpanel'} aria-labelledby={aba === 'pendentes' ? undefined : 'aba-grupo-cadastro'}>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <div className="relative min-w-[200px] flex-1">
                      <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar UC ou cliente"
                        aria-label="Buscar UC ou cliente" className={`${ESTILO_CAMPO} w-full pl-9`} />
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{linhas.length.toLocaleString('pt-BR')} UC(s)</span>
                  </div>

                  {marcadas.size > 0 && (
                    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3">
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{marcadas.size} selecionada(s)</span>
                      <label className="sr-only" htmlFor="destino-agrupamento">Agrupamento de destino</label>
                      <select id="destino-agrupamento" value={destino} onChange={e => setDestino(e.target.value)} className={ESTILO_CAMPO}>
                        <option value="">Mover para…</option>
                        {agrupamentos.filter(a => a.ativo && a.id !== selecionadoId).map(a => (
                          <option key={a.id} value={a.id}>{a.nome}</option>
                        ))}
                      </select>
                      <button type="button" disabled={!destino || agindo} aria-busy={agindo}
                        onClick={() => vincularMarcadas(Number(destino))}
                        title={!destino ? 'Escolha o agrupamento de destino' : undefined}
                        className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
                        {aba === 'pendentes' ? 'Vincular' : 'Mover'}
                      </button>
                      {aba === 'grupo' && (
                        <button type="button" disabled={agindo} onClick={() => setConfirmacao({
                          titulo: 'Retirar do agrupamento',
                          mensagem: `${marcadas.size} UC(s) voltam ao faturamento individual e saem do fechamento deste agrupamento. Continuar?`,
                          acao: desvincularMarcadas,
                        })}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20">
                          <UserMinus size={15} /> Retirar
                        </button>
                      )}
                      <button type="button" onClick={() => setMarcadas(new Set())} className="ml-auto text-sm font-medium text-primary hover:underline">
                        Limpar seleção
                      </button>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                        <tr>
                          <th className="w-10 px-3 py-2">
                            <input type="checkbox" aria-label="Selecionar as UCs desta página" checked={todasVisiveisMarcadas}
                              ref={el => { if (el) el.indeterminate = !todasVisiveisMarcadas && visiveis.some(u => marcadas.has(u.id)); }}
                              onChange={alternarTodas} disabled={visiveis.length === 0} className="rounded" />
                          </th>
                          <th className="px-3 py-2">UC</th>
                          <th className="px-3 py-2">Cliente</th>
                          <th className="px-3 py-2">Endereço</th>
                          {aba === 'pendentes' && <th className="px-3 py-2">CNPJ</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {visiveis.map(u => (
                          <tr key={u.id} className="align-top hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-3 py-2">
                              <input type="checkbox" checked={marcadas.has(u.id)} aria-label={`Selecionar UC ${u.numero_uc}`}
                                onChange={() => setMarcadas(a => { const n = new Set(a); n.has(u.id) ? n.delete(u.id) : n.add(u.id); return n; })}
                                className="rounded" />
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-900 dark:text-white">{u.numero_uc}</td>
                            <td className="max-w-[220px] truncate px-3 py-2 text-xs text-gray-700 dark:text-gray-200" title={u.nome_cliente ?? ''}>
                              {u.nome_cliente || '—'}
                            </td>
                            <td className="max-w-[260px] truncate px-3 py-2 text-xs text-gray-500 dark:text-gray-400" title={composeEnderecoUC(u as any)}>
                              {composeEnderecoUC(u as any) || 'Sem endereço'}
                            </td>
                            {aba === 'pendentes' && (
                              <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                                {cnpjBr(u.cpf_cnpj_cliente || u.cpf_cnpj_titular)}
                              </td>
                            )}
                          </tr>
                        ))}
                        {visiveis.length === 0 && (
                          <tr>
                            <td colSpan={aba === 'pendentes' ? 5 : 4} className="px-3 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                              {busca
                                ? 'Nenhuma UC com esse termo.'
                                : aba === 'pendentes'
                                  ? 'Nenhuma UC pendente: todas as UCs do pagador estão em um agrupamento.'
                                  : 'Nenhuma UC neste agrupamento. Use "Importar planilha" ou atribua pela lista de pendências.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {linhas.length > pageSize && (
                    <TablePagination total={linhas.length} pagina={pagina} pageSize={pageSize}
                      onPagina={setPagina} onPageSize={n => { setPageSize(n); setPagina(0); }} label="UCs" />
                  )}
                </div>
              )}

              {aba === 'grupo' && !selecionado && agrupamentos.length > 0 && (
                <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  Escolha um agrupamento na lista ao lado para ver e atribuir as UCs dele.
                </p>
              )}
            </>
          )}
        </section>
      </div>

      <AgrupamentoFaturamentoModal
        aberto={modalAberto}
        agrupamento={editando}
        onFechar={() => setModalAberto(false)}
        onSalvo={msg => { setAviso(msg); carregar(); }}
      />

      <ImportarAgrupamentoModal
        aberto={importarAberto}
        agrupamentos={agrupamentos}
        ucs={ucs}
        vinculoAtual={vinculoPorUc}
        onFechar={() => setImportarAberto(false)}
        onAplicado={msg => { setAviso(msg); carregar(); }}
      />

      <ImportarBorderoModal
        isOpen={borderoAberto}
        agrupamentos={agrupamentos}
        ucsDoAgrupamento={ucsDoAgrupamento}
        onClose={() => setBorderoAberto(false)}
        onConcluido={() => setSinalFaturas(n => n + 1)}
      />

      <ConfirmModal
        isOpen={!!confirmacao}
        onClose={() => setConfirmacao(null)}
        onConfirm={async () => { const c = confirmacao; setConfirmacao(null); await c?.acao(); }}
        title={confirmacao?.titulo}
        message={confirmacao?.mensagem ?? ''}
        loading={agindo}
        variant="warning"
      />
    </div>
  );
};

export default AgrupamentosFaturamentoView;
